import type { ExpiryTimeGranularity } from '@x-guard/xgac-types/xgac';
import {
  type DataProvider, useTranslate, useNotify, useRefresh, useDataProvider, Button, Form, useGetList, Confirm,
} from 'react-admin';
import { useState, useEffect, useMemo } from 'react';
import { useFormContext, useWatch, type FieldValues } from 'react-hook-form';
import {
  CircularProgress, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Stack,
} from '@mui/material';
import { Close as CloseIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import moment from 'moment';
import {
  type AssigneeChoice,
  splitRecipientSelection,
  resolveCertifiedAssignees,
  buildAssigneeChoices,
  getGroupIdsWithAppUsers,
  userCertificationTransformer,
  type ExistingCertifiedUser,
  getCoveredAssetIdsFromSelection,
} from '../../../utils/certifiedUsersAssignments';
import { CertifiedUsersSelectionInput } from './inputs/CertifiedUserSelectionInput';
import { notifyWithError } from '../../../utils/errorHandling';
import { hasCertificationDocument, type DocumentAssignee } from '../../../utils/certificationDocuments';
import {
  type CertificationUserDetails,
  type CertificationsByAssetId,
} from '../../../utils/certificationUserForm';
import { CertificationUserTable } from './CertificationUserTable';

type ModalStep = 'selectRecipients' | 'enterDetails';

export type CertifiedUsersCategory = {
  id?: string;
  name: string;
  validityPeriod: ExpiryTimeGranularity;
  isDocumentaryProofRequired: boolean;
};

export type CertifiedUsersFormValue = {
  recipientChoiceIds: string[];
  certificationsByAssetId: CertificationsByAssetId;
};

type AddCertifiedUsersModalProps = {
  open: boolean;
  onClose: () => void;
  categories: CertifiedUsersCategory[];
  onComplete?: () => void;
  onCreateCategories?: () => Promise<CertifiedUsersCategory[]>;
  choices?: AssigneeChoice[];
  certifiedAssetIds?: Set<string>;
  existingUsers?: ExistingCertifiedUser[];
  isChoicesLoading?: boolean;
};

const createUserCertification = (
  dataProvider: DataProvider,
  assetId: string,
  categoryId: string,
  details: CertificationUserDetails,
) => dataProvider.create('user-certifications', {
  data: userCertificationTransformer({
    certificationCategory: categoryId,
    firstIssuedOn: details.firstIssuedOn,
    lastReceivedOn: details.lastReceivedOn,
    expiryDate: details.expiryDate,
    plannedRenewalDate: null,
    ...(details.document?.rawFile ? { document: details.document } : {}),
  }),
  meta: { assetId },
});

const deleteUserCertification = (
  dataProvider: DataProvider,
  assetId: string,
  certificationId: string,
) => dataProvider.delete('user-certifications', {
  id: certificationId,
  previousData: { id: certificationId },
  meta: { assetId },
});

const mergeCertificationDetails = (
  resolvedAssetIds: string[],
  existing: CertificationsByAssetId | undefined,
  lastReceivedOn: string,
  expiryDate: string,
  includeFirstIssuedOn: boolean,
): CertificationsByAssetId => {

  const previous = existing ?? {};

  return Object.fromEntries(
    resolvedAssetIds.map((assetId) => [
      assetId,
      previous[assetId] ?? {
        ...(includeFirstIssuedOn ? { firstIssuedOn: lastReceivedOn } : {}),
        lastReceivedOn,
        expiryDate,
      },
    ]),
  );

};

const CertifiedUsersNextButton = (props: {
  onConfirmWithoutDetails: (values: FieldValues) => void | Promise<void>;
  initialRecipientIds: string[];
  step: ModalStep;
  choices: AssigneeChoice[];
  isExclusionLoading: boolean;
  validityPeriod?: CertifiedUsersCategory['validityPeriod'];
  certifiedAssetIds?: Set<string> | null;
  onRecipientsResolved: (recipients: DocumentAssignee[]) => void;
  onNext: (step: ModalStep) => void;
  onSkip: () => void | Promise<void>;
  isSubmitting: boolean;
}) => {

  const translate = useTranslate();
  const notify = useNotify();
  const { getValues, setValue } = useFormContext();
  const [isResolving, setIsResolving] = useState(false);

  const handleClick = async () => {

    if (props.step !== 'selectRecipients' || props.isSubmitting || props.isExclusionLoading || isResolving) return;

    setIsResolving(true);

    try {

      const selectedIds = (getValues('recipientChoiceIds') ?? []) as string[];
      const { userIds, groupIds } = splitRecipientSelection(selectedIds, props.choices);

      const resolvedSelection = await resolveCertifiedAssignees(userIds, groupIds);

      const knownChoiceIds = new Set(props.choices.map((c) => c.id));
      const unknownSelectedIds = selectedIds.filter((id) => !knownChoiceIds.has(id));
      const coveredIds = new Set([
        ...resolvedSelection.map((a) => a.id),
        ...unknownSelectedIds,
      ]);

      const selectableUserIds = new Set(
        props.choices
          .filter((c) => c.type === 'user')
          .map((c) => c.id),
      );
      const removedIds = props.initialRecipientIds.filter(
        (id) => selectableUserIds.has(id) && !coveredIds.has(id),
      );

      if (userIds.length === 0 && groupIds.length === 0 && removedIds.length === 0) {

        await props.onSkip();
        return;

      }

      const certifiedAssetIds = props.certifiedAssetIds ?? new Set<string>();
      const resolved = resolvedSelection.filter(
        (a) => !certifiedAssetIds.has(a.id),
      );

      if (resolved.length === 0) {

        if (removedIds.length > 0) {

          props.onRecipientsResolved([]);
          await props.onConfirmWithoutDetails(getValues());
          return;

        }

        notify(translate('resources.certification-categories.text.all_users_already_certified'), { type: 'warning' });
        return;

      }

      const today = moment().format('YYYY-MM-DD');
      const expiry = props.validityPeriod
        ? moment().add(props.validityPeriod.value, props.validityPeriod.type).format('YYYY-MM-DD')
        : today;

      const existing = getValues('certificationsByAssetId') as CertificationsByAssetId | undefined;

      setValue(
        'certificationsByAssetId',
        mergeCertificationDetails(
          resolved.map((r) => r.id),
          existing,
          today,
          expiry,
          true,
        ),
      );

      props.onRecipientsResolved(resolved);
      props.onNext('enterDetails');

    } finally {

      setIsResolving(false);

    }

  };

  return (
    <Button
      label="general.text.next"
      onClick={handleClick}
      disabled={isResolving || props.isExclusionLoading || props.isSubmitting}
      startIcon={isResolving ? <CircularProgress color="inherit" size={20} /> : undefined}
    />
  );

};

const CertifiedUsersConfirmButton = (props: {
  recipients: DocumentAssignee[];
  requiresDocumentaryProof: boolean;
  isSubmitting: boolean;
}) => {

  const certificationsByAssetId = useWatch({
    name: 'certificationsByAssetId',
  }) as CertificationsByAssetId | undefined;

  const documentsReady = !props.requiresDocumentaryProof
    || props.recipients.every((recipient) => (
      hasCertificationDocument(certificationsByAssetId?.[recipient.id]?.document)
    ));

  return (
    <Button
      type="submit"
      form="add-certified-users-form"
      label="ra.action.confirm"
      disabled={props.isSubmitting || !documentsReady}
      startIcon={props.isSubmitting ? <CircularProgress color="inherit" size={20} /> : undefined}
    />
  );

};

export const AddCertifiedUsersModal = (props: AddCertifiedUsersModalProps) => {

  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [step, setStep] = useState<ModalStep>('selectRecipients');
  const [recipients, setRecipients] = useState<DocumentAssignee[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validGroupIds, setValidGroupIds] = useState<Set<string> | null>(null);
  const [membersCountByGroupId, setMembersCountByGroupId] = useState<Map<string, number>>(new Map());
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const hasExternalChoices = props.choices !== undefined;

  const existingUsers = props.existingUsers ?? [];
  const primaryCategory = props.categories[0];
  const requiresDocumentaryProof = props.categories.some((c) => c.isDocumentaryProofRequired);

  const initialRecipientIds = useMemo(
    () => props.existingUsers?.map((u) => u.assetId) ?? [],
    [props.existingUsers],
  );

  const { data: users = [], isPending: isUsersPending } = useGetList('app-users', {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  }, { enabled: props.open && !hasExternalChoices });

  const { data: groups = [], isPending: isGroupsPending } = useGetList('asset-groups', {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  }, { enabled: props.open && !hasExternalChoices });

  const requestClose = () => {

    if (step === 'enterDetails') {

      setDiscardConfirmOpen(true);
      return;

    }

    props.onClose();

  };

  const confirmDiscard = () => {

    setDiscardConfirmOpen(false);
    props.onClose();

  };

  useEffect(() => {

    if (!props.open) {

      setDiscardConfirmOpen(false);

    }

  }, [props.open]);

  useEffect(() => {

    if (hasExternalChoices) {

      return undefined;

    }

    let cancelled = false;

    if (!props.open) {

      setValidGroupIds(null);
      setMembersCountByGroupId(new Map());

    } else if (groups.length === 0) {

      setValidGroupIds(new Set());
      setMembersCountByGroupId(new Map());

    } else {

      getGroupIdsWithAppUsers(groups).then(({
        validGroupIds: validIds,
        membersCountByGroupId: membersCountById,
      }) => {

        if (!cancelled) {

          setValidGroupIds(validIds);
          setMembersCountByGroupId(membersCountById);

        }

      });

    }

    return () => {

      cancelled = true;

    };

  }, [props.open, groups, hasExternalChoices]);

  const baseChoices = useMemo(() => {

    if (hasExternalChoices) return [];

    if (validGroupIds === null) {

      return buildAssigneeChoices(users, []);

    }

    return buildAssigneeChoices(
      users,
      groups.filter((group) => validGroupIds.has(String(group.id))),
      membersCountByGroupId,
    );

  }, [users, groups, validGroupIds, membersCountByGroupId, hasExternalChoices]);

  const defaultValues: CertifiedUsersFormValue = {
    recipientChoiceIds: initialRecipientIds,
    certificationsByAssetId: {},
  };

  const choices = hasExternalChoices ? props.choices : baseChoices;

  const resolvedCertifiedAssetIds = props.certifiedAssetIds ?? null;
  const isInternalLoading = !hasExternalChoices && (
    isUsersPending || isGroupsPending || (groups.length > 0 && validGroupIds === null)
  );
  const isChoicesLoading = props.isChoicesLoading ?? isInternalLoading;

  useEffect(() => {

    if (!props.open) {

      setStep('selectRecipients');
      setRecipients([]);

    }

  }, [props.open]);

  const subtitleKey = {
    selectRecipients: 'resources.certification-categories.text.certified_users_select_subtitle',
    enterDetails: 'resources.certification-categories.text.certified_users_dates_subtitle',
  }[step];

  const categoryLabel = props.categories.map((c) => c.name).join(', ');

  const handleSubmit = async (values: FieldValues) => {

    setIsSubmitting(true);

    try {

      const certifiedUsersValues = values as CertifiedUsersFormValue;
      const selectableUserIds = new Set(
        (choices ?? [])
          .filter((c) => c.type === 'user')
          .map((c) => c.id),
      );
      const coveredIds = await getCoveredAssetIdsFromSelection(
        certifiedUsersValues.recipientChoiceIds,
        choices ?? [],
      );
      const toRemove = existingUsers.filter(
        (u) => selectableUserIds.has(u.assetId) && !coveredIds.has(u.assetId),
      );

      let categories = props.categories;

      if (props.onCreateCategories) {

        categories = await props.onCreateCategories();

      }

      if (recipients.length === 0 && toRemove.length === 0) {

        notify(translate('resources.certification-categories.text.no_users_added'), { type: 'success' });
        refresh();
        props.onComplete?.();
        if (!props.onComplete) props.onClose();
        return;

      }

      await Promise.all([
        ...categories.flatMap((category) => {

          const categoryId = category.id;
          if (!categoryId) return [];

          return recipients.map((recipient) => {

            const details = certifiedUsersValues.certificationsByAssetId[recipient.id];
            if (!details) return Promise.resolve();
            return createUserCertification(dataProvider, recipient.id, categoryId, details);

          });

        }),
        ...toRemove.map((u) => deleteUserCertification(dataProvider, u.assetId, u.certificationId)),
      ]);

      const parts: string[] = [];

      if (recipients.length > 0) {

        parts.push(
          translate('resources.certification-categories.text.users_added', {
            smart_count: recipients.length,
          }),
        );

      }

      if (toRemove.length > 0) {

        parts.push(
          translate('resources.certification-categories.text.users_removed', {
            smart_count: toRemove.length,
          }),
        );

      }

      if (parts.length > 0) {

        notify(parts.join(', '), { type: 'success' });

      }

      refresh();
      props.onComplete?.();
      if (!props.onComplete) props.onClose();

    } catch (error) {

      notifyWithError(notify, error);

    } finally {

      setIsSubmitting(false);

    }

  };

  const handleBack = () => {

    if (step === 'enterDetails') {

      setRecipients([]);
      setStep('selectRecipients');

    }

  };

  const showConfirm = step === 'enterDetails';
  const showNext = step === 'selectRecipients';

  return (
    <>
      <Dialog
        open={props.open}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': { overflowY: 'hidden' },
          '& form': {
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minHeight: 0,
          },
        }}
      >
        <Form id="add-certified-users-form" defaultValues={defaultValues} onSubmit={handleSubmit} key={props.open ? 'open' : 'closed'}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              {step === 'enterDetails' && (
                <IconButton onClick={handleBack} aria-label={translate('ra.action.back')} disabled={isSubmitting}>
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Typography variant="body1" fontSize="20px">
                {translate('resources.certification-categories.text.certified_users_modal_title', { category: categoryLabel })}
              </Typography>
            </Stack>
            <IconButton onClick={requestClose} aria-label={translate('general.text.close')} disabled={isSubmitting}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {translate(subtitleKey, { name: categoryLabel })}
              </Typography>
              {step === 'selectRecipients' && (
                <CertifiedUsersSelectionInput
                  disabled={isChoicesLoading}
                  label="resources.certification-categories.fields.certified_users"
                  choices={choices ?? []}
                />
              )}
              {step === 'enterDetails' && primaryCategory && (
                <CertificationUserTable
                  recipients={recipients}
                  timeGranularity={primaryCategory.validityPeriod}
                  includeFirstIssuedOn
                  requiresDocument={requiresDocumentaryProof}
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button label="ra.action.cancel" onClick={requestClose} disabled={isSubmitting} />
            {showNext && (
              <CertifiedUsersNextButton
                onConfirmWithoutDetails={(values) => handleSubmit(values)}
                isExclusionLoading={isChoicesLoading}
                step={step}
                choices={choices ?? []}
                validityPeriod={primaryCategory?.validityPeriod}
                certifiedAssetIds={resolvedCertifiedAssetIds}
                onRecipientsResolved={setRecipients}
                onNext={setStep}
                onSkip={() => handleSubmit({ ...defaultValues, recipientChoiceIds: initialRecipientIds })}
                initialRecipientIds={initialRecipientIds}
                isSubmitting={isSubmitting}
              />
            )}
            {showConfirm && (
              <CertifiedUsersConfirmButton
                recipients={recipients}
                requiresDocumentaryProof={requiresDocumentaryProof}
                isSubmitting={isSubmitting}
              />
            )}
          </DialogActions>
        </Form>
      </Dialog>

      <Confirm
        isOpen={discardConfirmOpen}
        title="resources.certification-categories.text.discard_changes_title"
        content="resources.certification-categories.text.discard_changes_content"
        onClose={() => setDiscardConfirmOpen(false)}
        onConfirm={confirmDiscard}
      />
    </>

  );

};
